import { PopoverArrow } from "@radix-ui/react-popover";
import type { Column, RowData } from "@tanstack/react-table";
import { type ReactNode, useState } from "react";

import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Separator } from "../Separator";
import { FilterEditor } from "./FilterEditor";
import type { ColumnFilter } from "./filters/types";
import { getColumnSortInfo } from "./helpers";

interface IColumnFilterPopupProps<TData extends RowData, TValue> {
  /** Element used as the popover trigger — caller controls the look. */
  trigger: ReactNode;
  column: Column<TData, TValue>;
  columnId: string;
  filters: Partial<Record<string, ColumnFilter>>;
  onFilterChange: (columnId: string, filter: ColumnFilter | undefined) => void;
  onSortAsc: () => void;
  onSortDesc: () => void;
  onSortClear: () => void;
  isSortedAsc: boolean;
  isSortedDesc: boolean;
}

export function ColumnFilterPopup<TData extends RowData, TValue>({
  trigger,
  column,
  columnId,
  filters,
  onFilterChange,
  onSortAsc,
  onSortDesc,
  onSortClear,
  isSortedAsc,
  isSortedDesc,
}: IColumnFilterPopupProps<TData, TValue>) {
  const [open, setOpen] = useState(false);
  const filterType = column.columnDef.meta?.filterType;
  const columnType = column.columnDef.meta?.columnType;
  const filterOptions = column.columnDef.meta?.filterOptions ?? [];
  const filterPlaceholder = column.columnDef.meta?.filterPlaceholder;
  const activeFilter = filters[columnId];
  const hasActiveFilter = !!activeFilter;
  const canSort = column.getCanSort();

  const sortInfo = getColumnSortInfo(filterType, columnType);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => {
          // Radix would focus the first tabbable (a sort button). Focus only a filter's search
          // box, never e.g. a number-range input; preventScroll keeps the sticky header still.
          e.preventDefault();
          if (e.currentTarget instanceof HTMLElement)
            e.currentTarget
              .querySelector<HTMLElement>("[data-filter-search]")
              ?.focus({ preventScroll: true });
        }}
        align="center"
        side="bottom"
        className="w-auto max-w-[400px] p-3"
      >
        <PopoverArrow />
        <div className="flex flex-col gap-3">
          {/* Sort section */}
          {canSort && (
            <div className="flex flex-col gap-1">
              <p className="text-label">Sort</p>
              <div className="flex gap-1">
                <Button
                  variant={isSortedAsc ? "secondary" : "ghost"}
                  size="sm"
                  className="flex-1 gap-1.5 text-xs"
                  onClick={() => {
                    onSortAsc();
                    setOpen(false);
                  }}
                >
                  <sortInfo.asc.icon size={14} />
                  {sortInfo.asc.text}
                </Button>
                <Button
                  variant={isSortedDesc ? "secondary" : "ghost"}
                  size="sm"
                  className="flex-1 gap-1.5 text-xs"
                  onClick={() => {
                    onSortDesc();
                    setOpen(false);
                  }}
                >
                  <sortInfo.desc.icon size={14} />
                  {sortInfo.desc.text}
                </Button>
                {(isSortedAsc || isSortedDesc) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2 text-gray-500"
                    onClick={() => {
                      onSortClear();
                      setOpen(false);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Filter section */}
          {filterType && (
            <>
              {canSort && <Separator />}
              <div className="flex flex-col gap-2">
                <p className="text-label">Filter</p>
                <FilterEditor
                  filterType={filterType}
                  filterOptions={filterOptions}
                  filterPlaceholder={filterPlaceholder}
                  value={activeFilter}
                  onChange={(v) => onFilterChange(columnId, v)}
                />
                {hasActiveFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-destructive hover:text-destructive"
                    onClick={() => onFilterChange(columnId, undefined)}
                  >
                    Clear filter
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
